import { dataService } from '@app/modules/data';

import { DataObject, ObjectOptions } from './base';
import { PayAccount } from './payaccount';
import { Ticket } from './ticket';
import { TicketBAI } from './ticketbai';
import { VeriFactu } from './verifactu';
import { TicketSII } from './ticketsii';

export interface _AccountInvoice {
    account: number,
    since: Date,
    until: Date,
    series: string;
    invoice: string,
    amount: number,
    payment: string

    sentto?: string;
};

interface _AccountInvoiceData extends _AccountInvoice {
    objid?: number;
    _uuid?: string;
    created?: Date;
    updated?: Date;
};

abstract class AccountInvoiceData extends DataObject {
    protected _invoice: _AccountInvoiceData = {
        account: null,
        since: null,
        until: null,
        series: null,
        invoice: null,
        amount: null,
        payment: null
    };

    constructor(table: string, objid: string, data: dataService, objoptions: ObjectOptions){
        super(table, objid, data, objoptions);

        this._invoice.created = new Date();
        this._invoice.updated = new Date();
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    get created(){
        return this._invoice.created;
    }

    get updated(){
        return this._invoice.updated;
    }

    get account(): PayAccount {
        return this._children['account'] || null;
    }

    set account(value: PayAccount){
        if (this.SetChild('account', value, 'account')){
            this.ToUpdate = true;
        }
    }

    get since(): Date {
        return this._invoice.since;
    }

    set since(value: Date){
        if (this.patchValue(this._invoice, 'since', value)){
            this.ToUpdate = true;
        }
    }

    get until(): Date {
        return this._invoice.until;
    }

    set until(value: Date){
        if (this.patchValue(this._invoice, 'until', value)){
            this.ToUpdate = true;
        }
    }

    get amount(): number {
        return this._invoice.amount;
    }

    set amount(value: number){
        if (this.patchValue(this._invoice, 'amount', value)){
            this.ToUpdate = true;
        }
    }

    get payment(): string {
        return this._invoice.payment;
    }

    set payment(value: string) {
        if (this.patchValue(this._invoice, 'payment', value)){
            this.ToUpdate = true;
        }
    }

    /* read only properties */

    get series(): string {
        return this._invoice.series;
    }

    get invoice(): string {
        return this._invoice.invoice;
    }

    get sentto(): Array<string> {
        let _sentto = [];

        if (this._invoice.sentto){
            let _srvlst = [ 'TICEKTSII', 'TICKETBAI', 'VERIFACTU' ];

            let _server = Array.from(this._invoice.sentto);
            for(let i=0; i < _server.length; i++){
                if (_server[i] == '1'){
                    _sentto.push(_srvlst[i]);
                }
            }    
        }

        return _sentto;
    }

    get verifactu(): VeriFactu {
        let _verifactu = this._chldlist['verifactu'] || [];
        if (_verifactu.length > 0){
            return _verifactu[0];
        }
        return null;
    }

    get ticketbai(): TicketBAI {
        let _ticketbai = this._chldlist['ticketbai'] || [];
        if (_ticketbai.length > 0){
            return _ticketbai[0];
        }
        return null;
    }

    get ticketsii(): TicketSII {
        let _ticketsii = this._chldlist['ticketsii'] || [];
        if (_ticketsii.length > 0){
            return _ticketsii[0];
        }
        return null;
    }

    /****************************/
    /* COMMIT OPERATION         */
    /****************************/

    protected get Change() {
        return {
            account: this._invoice.account,
            since: this._invoice.since ? this.dateStrToMysql(this._invoice.since) : null,
            until: this._invoice.until ? this.dateStrToMysql(this._invoice.until) : null,
            series: this._invoice.series,
            invoice: this._invoice.invoice,
            amount: this._invoice.amount,
            payment: this._invoice.payment
        };
    }

    protected get Depend() {
        return {
            account: { item: this.account, relation_info: { to: 'invoices', by: 'account' } }   // this[by -> 'account'][to -> 'invoices'] => this
        };
    }

    protected get Children(){
        let _children = [];

        if (this.ticketbai){
            _children.push(this.ticketbai);
        }

        if (this.verifactu){
            _children.push(this.verifactu);
        }

        if (this.ticketsii){
            _children.push(this.ticketsii);
        }

        return _children;
    }

    /****************************/
    /* DATA OBJECT              */
    /****************************/
    
    private _patchData(_invoice: _AccountInvoice){
        let _toUpdate = false;

        _toUpdate = this.patchValue(this._invoice, 'account', _invoice['account']) || _toUpdate;
        _toUpdate = this.patchValue(this._invoice, 'since', _invoice['since']) || _toUpdate;
        _toUpdate = this.patchValue(this._invoice, 'until', _invoice['until']) || _toUpdate;
        _toUpdate = this.patchValue(this._invoice, 'series', _invoice['series']) || _toUpdate;
        _toUpdate = this.patchValue(this._invoice, 'invoice', _invoice['invoice']) || _toUpdate;
        _toUpdate = this.patchValue(this._invoice, 'amount', _invoice['amount']) || _toUpdate;
        _toUpdate = this.patchValue(this._invoice, 'sentto', _invoice['sentto']) || _toUpdate;
        _toUpdate = this.patchValue(this._invoice, 'payment', _invoice['payment']) || _toUpdate;

        return _toUpdate;
    }   

    set Data(_invoice: _AccountInvoice){
        this.patchValue(this._invoice, 'created', _invoice['created']);
        this.patchValue(this._invoice, 'updated', _invoice['updated']);

        if (this._patchData(_invoice)){
            this.ToUpdate = true;
        }
    }

    get Info(){
        return this._invoice;
    }

    set Info(value){
        this.DoPatchValues(value);
    }
   
    private DoPatchValues(_invoice: _AccountInvoice){
        this._patchData(_invoice);

        if (_invoice['account']){    // update children: 'account'
            let _objid = _invoice['account'].toString();
            this.SetChild('account', new PayAccount(_objid, this.data, this._objoptions), 'account')
        }
        else {
            this.SetChild('account', null, 'account');
        }
    }

    private _ddbb(info): _AccountInvoiceData {
        let _invoice: _AccountInvoiceData = {
            objid: info['objid'] ? parseInt(info['objid']) : null,
            created: new Date(Date.parse(this.mysqlToDateStr(info['created']))),
            updated: new Date(Date.parse(this.mysqlToDateStr(info['updated']))),
            account: info['account'] ? parseInt(info['account']) : null,
            since: new Date(Date.parse(this.mysqlToDateStr(info['since']))),
            until: new Date(Date.parse(this.mysqlToDateStr(info['until']))),
            series: info['series'],
            invoice: info['invoice'],
            amount: info['amount'] ? parseFloat(info['amount']) : null,
            payment: info['payment'],

            sentto: ('sentto' in info) ? info['sentto'] : null,
        };

        return _invoice;
    }

    protected _OnUpdate(info){
        let _invoice = this._ddbb(info);
        this.patchValue(this._invoice, 'objid', _invoice['objid']);
        this.patchValue(this._invoice, 'created', _invoice['created']);
        this.patchValue(this._invoice, 'updated', _invoice['updated']);
        this.DoPatchValues(_invoice);

        if (info['ticketbai']){   // update children: 'ticketbai'
            this.SetChildren <TicketBAI> (info['ticketbai'], 'ticketbai', TicketBAI, 'ticketbai');
        }

        if (info['verifactu']){   // update children: 'ticketbai'
            this.SetChildren <VeriFactu> (info['verifactu'], 'verifactu', VeriFactu, 'verifactu');
        }

        if (info['ticketsii']){   // update children: 'ticketsii'
            this.SetChildren <TicketSII> (info['ticketsii'], 'ticketsii', TicketSII, 'ticketsii');
        }
    }
}

export class AccountInvoice extends AccountInvoiceData {
    constructor(objid: string, data: dataService, objoptions: ObjectOptions = null){
        super('ACCOUNTINVOICE', objid, data, objoptions);
    }

    Copy(store: Array<DataObject> = []): AccountInvoice {
        return this._Copy(store) as AccountInvoice;
    }  

   /****************************/
    /* CHILDREN MANAGEMENT      */
    /****************************/

    AddTicket(child: Ticket){
        this.AddChild('tickets', child, 'place');
    }

    DelTicket(child: Ticket){
        this.DelChild('tickets', child, 'place');
    }

    async AddVeriFactu(){
        if (this.tickets.length == 0){
            console.error("[INVOICE] This account invoice is empty!");
            return;
        }

        if (this.place.SendVFTEnabled){
            if (this.verifactu){
                console.warn("[INVOICE] overwriting verifactu instance will bave no effect");
            }
    
            let _verifactu = new VeriFactu(null, this.data);
            if (_verifactu){
                _verifactu.invceac = this;
            }
    
            let _vfac = await _verifactu.Refresh();
            if (_vfac != null){
                this.AddChild('verifactu', _verifactu, 'invceac')    
            }

            return _vfac;
        }
    }

    async AddTicketBAI(){
        if (this.tickets.length == 0){
            console.error("[INVOICE] This account invoice is empty!");
            return;
        }

        if (this.place.SendBAIEnabled){
            if (this.ticketbai){
                console.warn("[INVOICE] overwriting ticketbai instance will bave no effect");
            }
    
            let _ticketbai = new TicketBAI(null, this.data);
            if (_ticketbai){
                _ticketbai.invceac = this;
            }
    
            let _tbai = await _ticketbai.Refresh();
            if (_tbai != null){
                this.AddChild('ticketbai', _ticketbai, 'invceac')    
            }

            return _tbai;
        }
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    get place(){
        return this.account?.place;
    }

    get payment(): string {
        let _payment = super.payment;
        
        switch(_payment){   // backwards compatibility
            case 'CASHPAY':
                return 'PAYCASH';
            case 'CARDPAY':
                return 'PAYCARD';
        }

        if (_payment == null){
            return 'UNKNOWN';
        }

        return _payment; 
    }

    set payment(value: string){
        super.payment = value;
    }

    /*
        Account invoice tickets are added only on creation
        The ticketlist is required for ticketbai xml generation
    */

    get tickets(){
        return this._chldlist['tickets'] || [];
    }

    get SplitTaxes(){
        let _taxrates = {};
        for(let _ticket of this.tickets){
            let _splitted = _ticket.SplitTaxes;
            for (let _taxrate of _splitted){
                if (!(_taxrate.rate in _taxrates)){
                    _taxrates[_taxrate.rate] = 0;
                }
                _taxrates[_taxrate.rate] += _taxrate.total;
            }
        }

        let _totaxes = [];

        for (let _taxrate in _taxrates){
            let _total = _taxrates[_taxrate];
            let _base = _total / ((100 + Number(_taxrate))/100);
            let _taxes = _total- _base;
    
            _totaxes.push({
                rate: Number(_taxrate),
                total: Math.round(_total * 100)/100,
                base: Math.round(_base * 100)/100,
                taxes: Math.round(_taxes * 100)/100
            })
        }

        return _totaxes;
    }


    get total(): number {
        return this.amount;
    }


    get sentto(): Array<string> {
        if (this._invoice.sentto){
            return super.sentto;
        }
        else {
            let _sentto = [];
            
            if (this.ticketbai?.IsValid){
                _sentto.push('TICKETBAI');
            }

            if (this.ticketsii?.IsValid){
                _sentto.push('TICKETSII');
            }

            if (this.verifactu?.IsValid){
                _sentto.push('VERIFACTU');
            }

            return _sentto;
        }
    }

    /****************************/
    /* CUSTOM METHODS           */
    /****************************/

    async SetInvoiceNumber(){
        this._invoice['series'] = 'A' + this.data.serial;
        this._invoice['invoice'] = await this.data.NextInvoice('A');
        
        this._toUpdate = true;
        this.DoRefresh('ACCOUNTINVOICE');
    }
}


